import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Copy, FolderInput, Mail, Send, Trash2 } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import MessageComposer from '@/features/messaging/MessageComposer'
import {
  useCreateFolder,
  useDeleteList,
  useDuplicateList,
  useListFolders,
  useListUsage,
  useMoveList,
  useSendToList,
} from '@/features/lists/useLists'
import { useDripSequences, useEnrollInDrip } from '@/features/drip/useDrip'
import { useContactsPage } from '@/features/contacts/useContacts'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { MESSAGE_TOKENS } from '@/features/messaging/tokens'
import type { ContactList } from '@/api/lists'
import type { FileRef } from '@/api/files'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** A compact "this list" summary at the top of an action dialog, so it's clear what is being acted on. */
function ListBadge({ list }: { list: ContactList }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 mb-4">
      <div className="text-sm font-semibold text-brand-darkest">{list.name}</div>
      <div className="text-xs text-gray-500">
        {list.memberCount.toLocaleString()} contact{list.memberCount === 1 ? '' : 's'} ·{' '}
        {list.listType === 'dynamic' ? 'Dynamic list' : 'Static list'}
      </div>
    </div>
  )
}

/** Folder picker with "+ New folder" inline, shared by Duplicate and Move. */
function FolderPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data: folders } = useListFolders()
  const createFolder = useCreateFolder()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  if (adding) {
    return (
      <div className="flex gap-2">
        <input
          autoFocus
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Folder name"
        />
        <button
          type="button"
          disabled={!name.trim() || createFolder.isPending}
          onClick={() =>
            createFolder.mutate(
              { name: name.trim() },
              { onSuccess: (f) => { onChange(f.id); setAdding(false); setName('') } },
            )
          }
          className={modalButton.primary}
        >
          Add
        </button>
        <button type="button" onClick={() => setAdding(false)} className={modalButton.ghost}>
          Cancel
        </button>
        {createFolder.isError && <p className="text-danger text-xs">{apiErrorMessage(createFolder.error)}</p>}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-3">
      <Select size="md" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1">
        <option value="">No folder</option>
        {(folders ?? []).map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </Select>
      <button type="button" onClick={() => setAdding(true)} className="text-sm font-medium text-brand-primary whitespace-nowrap">
        + New folder
      </button>
    </div>
  )
}

export function DuplicateListModal({ list, onClose, onDone }: { list: ContactList; onClose: () => void; onDone?: (copy: ContactList) => void }) {
  const duplicate = useDuplicateList()
  const [name, setName] = useState(`${list.name} copy`)
  const [folderId, setFolderId] = useState(list.folderId ?? '')
  const [keepMembers, setKeepMembers] = useState(true)
  const submit = () =>
    duplicate.mutate(
      { id: list.id, input: { name: name.trim(), folderId: folderId || null, keepMembers } },
      { onSuccess: (copy) => { onDone?.(copy); onClose() } },
    )
  return (
    <Modal
      title="Duplicate list"
      icon={<Copy size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        <>
          {duplicate.isError && <p className="text-danger text-xs mr-auto">{apiErrorMessage(duplicate.error)}</p>}
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button type="button" onClick={submit} disabled={!name.trim() || duplicate.isPending} className={modalButton.primary}>
            {duplicate.isPending ? 'Duplicating…' : 'Duplicate list'}
          </button>
        </>
      }
    >
      <ListBadge list={list} />
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">New list name</label>
          <input autoFocus className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <span className="block text-xs font-medium text-gray-500 mb-1">Folder</span>
          <FolderPicker value={folderId} onChange={setFolderId} />
        </div>
        <label className="flex items-start gap-2.5 text-sm">
          <input type="checkbox" className="mt-0.5" checked={keepMembers} onChange={(e) => setKeepMembers(e.target.checked)} />
          <span>
            <span className="font-medium text-brand-darkest">
              {list.listType === 'dynamic' ? 'Keep the same filters' : 'Copy the contacts'}
            </span>
            <span className="block text-xs text-gray-500">
              {list.listType === 'dynamic'
                ? 'The copy picks up the same contacts and keeps updating by itself.'
                : `All ${list.memberCount} contacts are added to the copy.`}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  )
}

export function MoveListModal({ list, onClose }: { list: ContactList; onClose: () => void }) {
  const move = useMoveList()
  const [folderId, setFolderId] = useState(list.folderId ?? '')
  return (
    <Modal
      title="Move to folder"
      icon={<FolderInput size={17} className="text-brand-primary" />}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button
            type="button"
            disabled={move.isPending || folderId === (list.folderId ?? '')}
            onClick={() => move.mutate({ id: list.id, folderId: folderId || null }, { onSuccess: onClose })}
            className={modalButton.primary}
          >
            {move.isPending ? 'Moving…' : 'Move list'}
          </button>
        </>
      }
    >
      <ListBadge list={list} />
      <FolderPicker value={folderId} onChange={setFolderId} />
      {move.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(move.error)}</p>}
    </Modal>
  )
}

export function DeleteListModal({ list, onClose, onDeleted }: { list: ContactList; onClose: () => void; onDeleted?: () => void }) {
  const del = useDeleteList()
  const { data: usage } = useListUsage(list.id)
  return (
    <Modal
      title="Delete list"
      tone="danger"
      icon={<AlertTriangle size={17} />}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button
            type="button"
            disabled={del.isPending}
            onClick={() => del.mutate(list.id, { onSuccess: () => { onDeleted?.(); onClose() } })}
            className={modalButton.danger}
          >
            <Trash2 size={14} className="inline -mt-0.5 mr-1" />
            {del.isPending ? 'Deleting…' : 'Delete list'}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        Delete <span className="font-semibold text-brand-darkest">{list.name}</span>? The contacts themselves stay —
        only the list goes. This can't be undone.
      </p>
      {usage && usage.length > 0 && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-xs text-red-700">
          This list was used to enrol contacts into {usage.length === 1 ? 'a sequence' : `${usage.length} sequences`}:{' '}
          {usage.map((u) => u.sequenceName).join(', ')}. Those contacts stay enrolled.
        </div>
      )}
      {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
    </Modal>
  )
}

export function AddToSequenceModal({ list, onClose }: { list: ContactList; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: sequences } = useDripSequences()
  const enroll = useEnrollInDrip()
  const [sequenceId, setSequenceId] = useState('')
  const [result, setResult] = useState<number | null>(null)
  return (
    <Modal
      title="Add to sequence"
      icon={<Mail size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        result != null ? (
          <button type="button" onClick={onClose} className={modalButton.primary}>Done</button>
        ) : (
          <>
            <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
            <button
              type="button"
              disabled={!sequenceId || enroll.isPending}
              onClick={() =>
                enroll.mutate(
                  { id: sequenceId, input: { listId: list.id } },
                  {
                    onSuccess: (r) => {
                      setResult(r.enrolled)
                      qc.invalidateQueries({ queryKey: ['lists'] }) // "Used in"
                    },
                  },
                )
              }
              className={modalButton.primary}
            >
              {enroll.isPending ? 'Adding…' : 'Add to sequence'}
            </button>
          </>
        )
      }
    >
      <ListBadge list={list} />
      {result != null ? (
        <p className="text-sm text-gray-700">
          {result === 0
            ? 'Everyone on this list is already in that sequence.'
            : `${result} contact${result === 1 ? '' : 's'} enrolled — the first email goes out on the sequence's schedule.`}
        </p>
      ) : (
        <>
          <label className="block text-xs font-medium text-gray-500 mb-1">Sequence</label>
          <Select size="md" value={sequenceId} onChange={(e) => setSequenceId(e.target.value)} className="w-full">
            <option value="">Choose a sequence…</option>
            {(sequences ?? []).map((s) => (
              <option key={s.id} value={s.id} disabled={s.steps.length === 0}>
                {s.name}
                {s.steps.length === 0 ? ' (no steps yet)' : ` · ${s.steps.length} step${s.steps.length === 1 ? '' : 's'}`}
              </option>
            ))}
          </Select>
          <p className="text-xs text-gray-500 mt-2">
            Contacts on the list now are enrolled once; anyone already in the sequence is skipped.
          </p>
          {enroll.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(enroll.error)}</p>}
        </>
      )}
    </Modal>
  )
}

export function SendToListModal({ list, onClose }: { list: ContactList; onClose: () => void }) {
  const send = useSendToList()
  const [channel, setChannel] = useState('email')
  const [templateId, setTemplateId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<FileRef[]>([])
  const [result, setResult] = useState<{ sent: number; queued: number; skipped: number } | null>(null)
  // every recipient gets their own render — preview it for the first one on the list
  const { data: first } = useContactsPage({ list: list.id, page: 0, size: 1 })
  const sample = first?.items[0]
  return (
    <Modal
      title={`Send to ${list.name}`}
      icon={<Send size={17} className="text-brand-primary" />}
      onClose={onClose}
      size="lg"
      footer={result ? <button type="button" onClick={onClose} className={modalButton.primary}>Done</button> : undefined}
    >
      {result ? (
        <p className="text-sm text-gray-700">
          {result.queued > 0
            ? `${result.queued} messages are being sent in the background.`
            : `Sent to ${result.sent} contact${result.sent === 1 ? '' : 's'}${result.skipped ? ` · ${result.skipped} skipped (opted out or no address)` : ''}.`}
        </p>
      ) : (
        <>
          <MessageComposer
            inline
            channel={channel}
            onChannelChange={setChannel}
            templateId={templateId}
            onTemplateChange={setTemplateId}
            subject={subject}
            onSubjectChange={setSubject}
            body={body}
            onBodyChange={setBody}
            recipientCount={list.memberCount}
            sampleContact={sample}
            previewTarget={sample ? { kind: 'contact', id: sample.id } : undefined}
            tokens={MESSAGE_TOKENS.filter((t) => channel === 'email' || t !== '{{agent_signature}}')}
            sending={send.isPending}
            allowAttachments
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            onSend={({ includeSignature }) =>
              send.mutate(
                {
                  listId: list.id,
                  input: {
                    channel,
                    templateId: templateId || null,
                    subject,
                    body,
                    attachments: channel === 'email' && attachments.length
                      ? attachments.map((f) => ({ key: f.key, filename: f.filename, contentType: f.contentType }))
                      : undefined,
                    includeSignature,
                  },
                },
                { onSuccess: setResult },
              )
            }
          />
          {send.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(send.error)}</p>}
        </>
      )}
    </Modal>
  )
}
